import React from 'react';

import { routes } from '@aline/routes';
import { Typography } from '@mui/material';
import clsx from 'clsx';
import { useMatch } from 'react-router-dom';
import { AlineAvatar, AlineTooltip, useCellStyles } from 'src/components';
import { useCurrentUserId } from 'src/gql';
import type { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

type OwnerCellOwner = Pick<Apollo.User, 'avatarColor' | 'displayName' | 'id' | '__typename'>;

interface OwnerRow {
  owner: OwnerCellOwner;
}

const useOwnerWithNameStyles = makeStyles()(theme => ({
  ownerWithName: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    minWidth: 0,
  },
}));

export function getIsOwnerCellValue(value: unknown): value is Partial<OwnerCellOwner> {
  return value !== null && typeof value === 'object';
}

// Use the row instead of the value in GridCellParams b/c in some cases we mutate the value (e.g., via valueGetter func) to make owner filterable.
export type OwnerCellProps = { row: OwnerRow; showName?: boolean };

export function OwnerCell({ row: { owner }, showName = false }: OwnerCellProps): JSX.Element {
  const classes = useCellStyles();
  const { classes: ownerWithNameClasses } = useOwnerWithNameStyles();
  const currentUserId = useCurrentUserId();
  const isOwnerCurrentUser = owner.id === currentUserId;
  const reportRouteMatch = useMatch({ end: false, path: routes.ReportDetail });
  const isAtReportRoute = Boolean(reportRouteMatch);

  if (isOwnerCurrentUser)
    return (
      <div className={clsx(isAtReportRoute && classes.cell)}>
        <Typography variant="body1">me</Typography>
      </div>
    );

  if (showName)
    return (
      <div className={clsx(ownerWithNameClasses.ownerWithName, isAtReportRoute && classes.cell)}>
        <AlineAvatar avatarInfo={owner} />
        <Typography noWrap variant="body1">
          {owner.displayName}
        </Typography>
      </div>
    );

  return (
    <div className={clsx(isAtReportRoute && classes.cell)}>
      <AlineTooltip arrow placement="top" title={owner.displayName}>
        <AlineAvatar avatarInfo={owner} />
      </AlineTooltip>
    </div>
  );
}
